import type { SaleStatusFilter, SaleFiscalStatusFilter, SaleSortKey, SaleSortDir } from '../domain/salesTypes';
import type { SaleChannel } from '../domain/salesTypes';

export type SaleChannelFilter = 'all' | SaleChannel;

/** Cláusula SQLite para filtrar ventas con cobro pendiente (misma semántica que el listado remoto). */
export function salesListPaymentPendingSqlClause(paymentPendingOnly?: boolean): string {
  if (!paymentPendingOnly) return '';
  return " AND COALESCE(s.payment_status, 'pending') != 'paid'";
}

/** Filtro por estado de venta (activa/anulada). */
export function salesListStatusSqlClause(statusFilter?: SaleStatusFilter): string {
  if (!statusFilter || statusFilter === 'all') return '';
  if (statusFilter === 'voided') return " AND COALESCE(s.status, 'active') = 'voided'";
  return " AND COALESCE(s.status, 'active') != 'voided'";
}

/** Filtro por medio de cobro (method_id). Usa EXISTS contra sale_payments. */
export function salesListPaymentMethodSqlClause(methodId?: string): string {
  if (!methodId) return '';
  return ' AND EXISTS (SELECT 1 FROM sale_payments sp WHERE sp.sale_id = s.id AND sp.method_id = ?)';
}

/** Filtro por estado fiscal. */
export function salesListFiscalStatusSqlClause(fiscalFilter?: SaleFiscalStatusFilter): string {
  if (!fiscalFilter || fiscalFilter === 'all') return '';
  if (fiscalFilter === 'non_fiscal') return " AND COALESCE(s.fiscal_mode, 'fiscal') = 'non_fiscal'";
  if (fiscalFilter === 'fiscal') return " AND COALESCE(s.fiscal_mode, 'fiscal') = 'fiscal'";
  if (fiscalFilter === 'pending_sync') return " AND COALESCE(s.fiscal_status, 'pending_sync') = 'pending_sync'";
  return '';
}

/** Filtro por canal (mostrador / online). */
export function salesListChannelSqlClause(channelFilter?: SaleChannelFilter): string {
  if (!channelFilter || channelFilter === 'all') return '';
  if (channelFilter === 'online') return " AND COALESCE(s.channel, 'pos') = 'online'";
  return " AND COALESCE(s.channel, 'pos') = 'pos'";
}

/** Predicado equivalente para filtrar filas en memoria (p. ej. merge de no sincronizadas). */
export function saleRowMatchesPaymentPendingFilter(
  paymentStatus: string | null | undefined,
  paymentPendingOnly?: boolean
): boolean {
  if (!paymentPendingOnly) return true;
  return (paymentStatus ?? 'pending') !== 'paid';
}

/** Whitist de columnas ordenables → expresión SQL segura (sin interpolación de input). */
const SORT_COLUMN_MAP: Record<SaleSortKey, string> = {
  created_at: 's.created_at',
  total: 's.total',
  cashier_user_id: 's.cashier_user_id',
};

/** Cláusula ORDER BY dinámica y segura (sólo keys del whitelist). */
export function salesListOrderByClause(sortKey?: SaleSortKey, sortDir?: SaleSortDir): string {
  const column = SORT_COLUMN_MAP[sortKey ?? 'created_at'] ?? 's.created_at';
  const dir = sortDir === 'asc' ? 'ASC' : 'DESC';
  return `${column} ${dir}`;
}
